export const DEFAULT_TIMEZONE = "America/Toronto";
export const DEFAULT_TARGET_SECONDS = 16 * 60 * 60;
export function isValidTimeZone(timeZone) {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
export function pad2(value) {
    return String(value).padStart(2, "0");
}
export function parseDateKey(dateKey) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
    if (!match) {
        throw new Error(`Invalid date key: ${dateKey}`);
    }
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3])
    };
}
export function dateKeyFromParts(year, month, day) {
    return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
}
export function addDaysToDateKey(dateKey, days) {
    const { year, month, day } = parseDateKey(dateKey);
    const date = new Date(Date.UTC(year, month - 1, day + days));
    return dateKeyFromParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
export function compareDateKeys(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
export function getZonedParts(epochMs, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        calendar: "iso8601",
        numberingSystem: "latn",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23"
    });
    const values = Object.fromEntries(formatter
        .formatToParts(new Date(epochMs))
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, part.value]));
    return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
        hour: Number(values.hour),
        minute: Number(values.minute),
        second: Number(values.second)
    };
}
export function formatLocalDateKey(epochMs, timeZone = DEFAULT_TIMEZONE) {
    const parts = getZonedParts(epochMs, timeZone);
    return dateKeyFromParts(parts.year, parts.month, parts.day);
}
export function formatMyFastHabitDate(epochMs, timeZone = DEFAULT_TIMEZONE) {
    const parts = getZonedParts(epochMs, timeZone);
    return `${pad2(parts.month)}-${pad2(parts.day)}-${String(parts.year).padStart(4, "0")}`;
}
export function formatMyFastHabitTime(epochMs, timeZone = DEFAULT_TIMEZONE) {
    const { hour, minute } = getZonedParts(epochMs, timeZone);
    const suffix = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${pad2(hour12)}:${pad2(minute)} ${suffix}`;
}
export function zonedTimeToEpochMs(parts, timeZone = DEFAULT_TIMEZONE) {
    const desiredSecond = parts.second ?? 0;
    let utcMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, desiredSecond);
    const desiredAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, desiredSecond);
    for (let attempt = 0; attempt < 4; attempt += 1) {
        const zoned = getZonedParts(utcMs, timeZone);
        const zonedAsUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
        const delta = desiredAsUtc - zonedAsUtc;
        if (delta === 0) {
            return utcMs;
        }
        utcMs += delta;
    }
    const finalParts = getZonedParts(utcMs, timeZone);
    if (finalParts.year !== parts.year ||
        finalParts.month !== parts.month ||
        finalParts.day !== parts.day ||
        finalParts.hour !== parts.hour ||
        finalParts.minute !== parts.minute ||
        finalParts.second !== desiredSecond) {
        throw new Error(`Could not resolve local time in ${timeZone}`);
    }
    return utcMs;
}
export function parseMyFastHabitDate(dateValue) {
    const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateValue.trim());
    if (!match) {
        throw new Error(`Invalid MyFastHabit date: ${dateValue}`);
    }
    return {
        month: Number(match[1]),
        day: Number(match[2]),
        year: Number(match[3])
    };
}
export function parseMyFastHabitTime(timeValue) {
    const match = /^(\d{1,2}):(\d{2})\s*([ap]m)$/i.exec(timeValue.trim());
    if (!match) {
        throw new Error(`Invalid MyFastHabit time: ${timeValue}`);
    }
    const rawHour = Number(match[1]);
    const minute = Number(match[2]);
    const meridiem = match[3]?.toLowerCase();
    if (rawHour < 1 || rawHour > 12 || minute < 0 || minute > 59) {
        throw new Error(`Invalid MyFastHabit time: ${timeValue}`);
    }
    return {
        hour: meridiem === "pm" ? (rawHour === 12 ? 12 : rawHour + 12) : rawHour === 12 ? 0 : rawHour,
        minute
    };
}
export function durationSeconds(startTs, endTs) {
    return Math.round((endTs - startTs) / 1000);
}
export function todayDateKey(timeZone = DEFAULT_TIMEZONE, nowMs = Date.now()) {
    return formatLocalDateKey(nowMs, timeZone);
}
export function startOfWeekDateKey(dateKey, weekStart) {
    const { year, month, day } = parseDateKey(dateKey);
    const date = new Date(Date.UTC(year, month - 1, day));
    const utcDay = date.getUTCDay();
    const offset = weekStart === "mon" ? (utcDay + 6) % 7 : utcDay;
    return addDaysToDateKey(dateKey, -offset);
}
export function statsDateRange(range, timeZone = DEFAULT_TIMEZONE, weekStart = "mon", nowMs = Date.now()) {
    const today = todayDateKey(timeZone, nowMs);
    if (range === "this_week") {
        return { fromDate: startOfWeekDateKey(today, weekStart), toDate: today };
    }
    if (range === "last_7d") {
        return { fromDate: addDaysToDateKey(today, -6), toDate: today };
    }
    return { fromDate: addDaysToDateKey(today, -29), toDate: today };
}
